import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Dice5, Disc3, Palette, Dices, Sparkles, ArrowRight, Trophy, Gift, Coins } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import WinnersMarquee from "../components/WinnersMarquee";

const feats = [
  { icon: Disc3, key: "spinWheel" },
  { icon: Palette, key: "colorGame" },
  { icon: Dices, key: "diceGame" },
  { icon: Sparkles, key: "scratchGame" },
];

export default function Landing() {
  const { t, lang, toggle } = useLang();
  const { user } = useAuth();

  return (
    <div className="min-h-screen grid-noise">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070B1F]/80 border-b border-amber-500/20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Bam Bam Bhole" className="h-9 w-9 rounded-xl object-cover ring-1 ring-amber-500/40" />
            <span className="font-display text-xl font-extrabold gold-text">{t("brand")}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggle} className="rounded-full border border-cyan-400/30 bg-black/30 px-3 py-1.5 text-xs font-bold text-cyan-300" data-testid="lang-toggle-btn">
              {lang === "hi" ? "EN" : "हिंदी"}
            </button>
            <Link to={user ? "/app" : "/login"} data-testid="header-login-btn" className="btn-gold rounded-full px-5 py-1.5 text-sm">
              {user ? t("playNow") : t("login")}
            </Link>
          </div>
        </div>
      </header>

      <WinnersMarquee />

      <section className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-24 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <img src="/brand_logo.jpg" alt="Bam Bam Bhole" data-testid="hero-brand-logo" className="mx-auto mb-6 h-44 w-44 sm:h-60 sm:w-60 rounded-[2rem] object-cover ring-2 ring-amber-400/40 animate-trishul" />
          <p className="om-chant font-display text-sm sm:text-base font-bold mb-4" data-testid="hero-chant">॥ हर हर महादेव ॥</p>
          <span className="inline-block rounded-full border border-amber-500/40 bg-black/40 px-4 py-1.5 text-xs font-semibold tracking-wider uppercase text-amber-300 mb-6">
            {t("tagline")}
          </span>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] gold-text max-w-4xl mx-auto">
            {t("brand")}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">{t("heroSub")}</p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to={user ? "/app" : "/login"} data-testid="hero-cta-btn" className="btn-gold rounded-full px-8 py-3.5 text-lg flex items-center gap-2 animate-pulse-glow">
              {t("playNow")} <ArrowRight size={20} />
            </Link>
          </div>
          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-slate-400">
            <span className="flex items-center gap-1.5"><Coins size={16} className="text-amber-400" /> ₹ {t("wallet")}</span>
            <span className="flex items-center gap-1.5"><Trophy size={16} className="text-cyan-400" /> {t("leaderboard")}</span>
          </div>
        </motion.div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {feats.map((f, i) => (
            <motion.div key={f.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
              className="mw-card mw-card-hover p-6 flex flex-col items-center gap-3">
              <div className="grid place-items-center h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-400/20 to-purple-600/10 border border-white/10">
                <f.icon size={26} className="text-amber-300" />
              </div>
              <span className="font-display font-bold text-center">{t(f.key)}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-amber-500/15 py-6 text-center text-xs text-slate-500">
        {t("disclaimer")}
      </footer>
    </div>
  );
}
